import React, { Component } from 'react'
import { PropTypes } from 'prop-types'

import { Button, Container, Input } from '@sainsburys-tech/fable'

import LoadingOverlay from '../LoadingOverlay'
import api from '../../utils/api'
import userManager from '../../utils/identity'
import errorIcon from '../../assets/error.png'
import { setRememberMe, removeToken } from '../../utils/storage'

import '../../sass/components/change-password.scss'

class ChangePassword extends Component {
  constructor(props) {
    super(props)
    this.state = {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      oldPasswordFieldFocus: false,
      newPasswordFieldBlured: false,
      confirmPasswordBlured: false,
      loading: false,
      oldPasswordFieldError: false,
      sessionError: false
    }
  }

  componentDidMount() {
    const { setHeader, setSignOutHeader } = this.props
    setHeader()
    setSignOutHeader(false)
  }

  checkAllFieldsValid = () => {
    const regex = /^(.{0,11}|[^0-9]*|[^A-Z]*|[a-zA-Z0-9]*)$/ // must be min length of 12, 1 uppercase, 1 number and 1 symbol
    const { newPassword, confirmPassword, oldPassword } = this.state

    if (newPassword === ''
      || confirmPassword === ''
      || oldPassword === ''
      || (newPassword !== confirmPassword)
      || (newPassword === oldPassword)
      || (regex.test(newPassword))
    ) {
      return false
    }
    return true
  }

  setOldPasswordFocus = () => {
    this.setState({ oldPasswordFieldError: false })
  }

  setNewPasswordBlur = () => {
    this.setState({ newPasswordFieldBlured: true })
  }

  setConfirmPasswordBlur = () => {
    this.setState({ confirmPasswordBlured: true })
  }

  newPasswordFieldError = () => {
    const { newPassword, newPasswordFieldBlured, oldPassword } = this.state
    const regex = /^(.{0,11}|[^0-9]*|[^A-Z]*|[a-zA-Z0-9]*)$/ // must be min length of 12, 1 uppercase, 1 number and 1 symbol

    if (regex.test(newPassword) && newPasswordFieldBlured) {
      return 'Password doesn\'t match the required format, please choose a different password'
    }
    if (newPassword === oldPassword && newPassword !== '' && newPasswordFieldBlured) {
      return 'New password can not be same as old password, please choose a different password'
    }
    return ''
  }

  getPasswordRequirements = () => {
    const { newPassword } = this.state

    return [
      { label: 'at least 1 uppercase letter', isValid: /[A-Z]/.test(newPassword) },
      { label: 'at least 1 number', isValid: /[0-9]/.test(newPassword) },
      { label: 'at least 1 special character (!*.-?)', isValid: /[^a-zA-Z0-9]/.test(newPassword) },
      { label: 'at least 12 characters long', isValid: newPassword.length >= 12 }
    ]
  }

  confirmPasswordFieldError = () => {
    const { newPassword, confirmPassword, confirmPasswordBlured } = this.state

    if (confirmPassword !== newPassword && confirmPasswordBlured) {
      return 'New password doesn\'t match, please enter the same password to confirm'
    }
    return ''
  }

  handleTextChange = (value, key) => {
    const nextValue = value || ''
    this.setState({ [key]: nextValue })
  }

  handleResetPassword = async () => {
    const { history, setLoggedInUser } = this.props
    const { oldPassword, newPassword } = this.state

    this.setState({ loading: true, sessionError: false, oldPasswordFieldError: false })

    try {
      const oidcUser = await userManager.getUser()
      const userId = oidcUser && oidcUser.profile ? oidcUser.profile.sub : null

      if (!userId) {
        this.setState({ loading: false, sessionError: true })
        return
      }

      const res = await api.validateAndResetPassword(userId, oldPassword, newPassword)

      if (res === 200) {
        removeToken()
        setRememberMe(false)
        setLoggedInUser(null)
        sessionStorage.clear()
        document.cookie = 'SignInBookSession=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        document.cookie = 'idsrv.session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        history.push('/resetsuccess')
      } else if (res === 401) {
        this.setState({ oldPasswordFieldError: true, loading: false })
      } else {
        this.setState({ loading: false })
        history.push('/reseterror')
      }
    } catch (e) {
      this.setState({ loading: false })
      history.push('/reseterror')
    }
  }

  render() {
    const {
      oldPassword, newPassword, confirmPassword, loading, sessionError
    } = this.state
    const newPasswordErrorText = this.newPasswordFieldError()
    const confirmPasswordErrorText = this.confirmPasswordFieldError()
    const passwordRequirements = this.getPasswordRequirements()
    let errorText = ''
    if (newPasswordErrorText !== '') {
      errorText = newPasswordErrorText
    } else if (confirmPasswordErrorText !== '') {
      errorText = confirmPasswordErrorText
    }

    return (
      <Container as="div" className="dsib-changePassword">
        <div className="dsib-changePassword__content">
          <div className="dsib-changePassword__panel">
            <h2 className="dsib-changePassword__title">Change Password</h2>

            <div className="dsib-changePassword__form">
              {sessionError && (
                <div className="dsib-changePassword__error" role="alert">
                  <img className="dsib-changePassword__errorIcon" src={errorIcon} alt="" aria-hidden="true" />
                  <div>Your session has expired. Please sign in again and retry.</div>
                </div>
              )}
              <div className={`dsib-changePassword__field ${this.state.oldPasswordFieldError ? 'dsib-changePassword__field--error' : ''}`}>
                <Input
                  label="Old password"
                  value={oldPassword}
                  autocomplete="current-password"
                  type="password"
                  onChange={value => this.handleTextChange(value, 'oldPassword')}
                  onFocus={this.setOldPasswordFocus}
                  inputProps={{
                    id: 'txtOldPassword',
                    name: 'txtOldPassword'
                  }}
                />
                {this.state.oldPasswordFieldError && (
                  <div className="dsib-changePassword__error" role="alert">
                    <img className="dsib-changePassword__errorIcon" src={errorIcon} alt="" aria-hidden="true" />
                    <div>Please enter correct password</div>
                  </div>
                )}
              </div>

              <div className={`dsib-changePassword__field ${newPasswordErrorText !== '' ? 'dsib-changePassword__field--error' : ''}`}>
                <div className="dsib-changePassword__label">New password</div>
                <p className="dsib-changePassword__help">Please enter a new password in the following format:</p>
                <ul className="dsib-changePassword__list">
                  {passwordRequirements.map(requirement => (
                    <li
                      key={requirement.label}
                      className={`dsib-changePassword__requirement dsib-changePassword__requirement--${requirement.isValid ? 'valid' : 'invalid'}`}
                    >
                      {requirement.label}
                    </li>
                  ))}
                </ul>
                <div className="dsib-changePassword__newPasswordInput">
                  <Input
                    label="New password"
                    value={newPassword}
                    hasHiddenLabel
                    autocomplete="new-password"
                    type="password"
                    onChange={value => this.handleTextChange(value, 'newPassword')}
                    onBlur={this.setNewPasswordBlur}
                    inputProps={{
                      id: 'txtNewPassword',
                      name: 'txtNewPassword',
                    }}
                  />
                </div>
                {newPasswordErrorText !== '' && (
                  <div className="dsib-changePassword__error" role="alert">
                    <img className="dsib-changePassword__errorIcon" src={errorIcon} alt="" aria-hidden="true" />
                    <div>{newPasswordErrorText}</div>
                  </div>
                )}
              </div>

              <div className={`dsib-changePassword__field ${confirmPasswordErrorText !== '' || errorText !== '' ? 'dsib-changePassword__field--error' : ''}`}>
                <Input
                  label="Confirm new password"
                  value={confirmPassword}
                  autocomplete="new-password"
                  type="password"
                  onChange={value => this.handleTextChange(value, 'confirmPassword')}
                  onBlur={this.setConfirmPasswordBlur}
                  inputProps={{
                    id: 'txtConfirmNewPassword',
                    name: 'txtConfirmNewPassword',
                  }}
                />
                {errorText !== '' && (
                  <div className="dsib-changePassword__error" role="alert">
                    <img className="dsib-changePassword__errorIcon" src={errorIcon} alt="" aria-hidden="true" />
                    <div>{errorText}</div>
                  </div>
                )}
              </div>

              <div className="dsib-changePassword__actions">
                <Button
                  className="dsib-changePassword__button"
                  type="button"
                  disabled={!this.checkAllFieldsValid() || loading}
                  onClick={this.handleResetPassword}
                  variant="primary"
                >
                  Change Password
                </Button>
              </div>
            </div>
          </div>
        </div>
        {loading
          && <LoadingOverlay hasBackground text="Changing your password..." />
        }
      </Container>
    )
  }
}

ChangePassword.propTypes = {
  user: PropTypes.shape({
    username: PropTypes.string
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  setLoggedInUser: PropTypes.func.isRequired,
  setHeader: PropTypes.func.isRequired,
  setSignOutHeader: PropTypes.func.isRequired
}

ChangePassword.defaultProps = {
  user: {
    username: null
  }
}

export default ChangePassword
